import type { ComponentType } from 'react'
import type { CrumbMeta } from '@/bootstrap/typings'
import type {
	EntityCreatePageProps,
	EntityDetailPageProps,
} from '@/components/entity-page-views'
import { general_create } from '@/integrations/paraglide/messages/_index.js'
import { getEntityLabel, getEntityTabLabel } from '@/lib/entity-route-i18n'

export type EntityPageRouteKind = 'create' | 'detail'

type BreadcrumbFactory = (args: {
	params: Record<string, string | number>
	search: Record<string, string | number>
}) => CrumbMeta

export type EntityPageRouteOverride = {
	entity: string
	route: EntityPageRouteKind
	breadcrumb?: BreadcrumbFactory
	closePath?: (entity: string) => string
	createdPath?: (entity: string, id?: string | number) => string | undefined
	createComponent?: ComponentType<EntityCreatePageProps>
	detailComponent?: ComponentType<EntityDetailPageProps>
}

type EntityPageOverrides = Partial<
	Record<string, Partial<Record<EntityPageRouteKind, EntityPageRouteOverride>>>
>

type EntityPageOverrideModule = {
	entityPageOverride?: EntityPageRouteOverride
}

function getDefaultCreateBreadcrumb(entity: string): CrumbMeta {
	return {
		label: `${getEntityLabel(entity)} · ${general_create()}`,
	}
}

function getDefaultDetailBreadcrumb(): CrumbMeta {
	return {
		label: getEntityTabLabel('detail'),
	}
}

const overrideModules = import.meta.glob<EntityPageOverrideModule>(
	'../entity-page-overrides/*.tsx',
	{ eager: true },
)

export const entityPageOverrides = Object.values(overrideModules).reduce(
	(accumulator, module) => {
		const override = module.entityPageOverride
		if (!override) {
			return accumulator
		}

		const entityOverrides = accumulator[override.entity] ?? {}
		entityOverrides[override.route] = override
		accumulator[override.entity] = entityOverrides
		return accumulator
	},
	{} as EntityPageOverrides,
)

export function getEntityPageOverride(
	entity: string,
	route: EntityPageRouteKind,
) {
	return entityPageOverrides[entity]?.[route]
}

export function getEntityPageBreadcrumb(
	entity: string,
	route: EntityPageRouteKind,
	args: {
		params: Record<string, string | number>
		search: Record<string, string | number>
	},
): CrumbMeta {
	const override = getEntityPageOverride(entity, route)
	return (
		override?.breadcrumb?.(args) ??
		(route === 'create'
			? getDefaultCreateBreadcrumb(entity)
			: getDefaultDetailBreadcrumb())
	)
}

export function getEntityCreateClosePath(entity: string): string {
	return (
		getEntityPageOverride(entity, 'create')?.closePath?.(entity) ?? `/${entity}`
	)
}

export function getEntityCreateCreatedPath(
	entity: string,
	id?: string | number,
): string | undefined {
	if (id === undefined) {
		return undefined
	}

	return (
		getEntityPageOverride(entity, 'create')?.createdPath?.(entity, id) ??
		`/${entity}/${String(id)}/overview`
	)
}
